import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { loadYouTubeIframeApi, type YTPlayer } from '../lib/youtubeIframeApi'
import { segmentRangeError, youtubeVideoId } from '../lib/youtube'
import { clampSeekTime } from '../lib/playbackSeek'
import { PlaybackSeekControls } from './PlaybackSeekControls'
import { VolumeControl } from './PlaybackPreferenceControls'
import { useI18n } from '../i18n/useI18n'

export interface YouTubeAuthoringPreviewHandle {
  getCurrentTime(): number | null
  getDuration(): number | null
  previewRange(startTime: number, endTime: number): string | null
}

interface Props {
  youtubeUrl: string
  title: string
  onDurationChange: (duration: number | null) => void
  onMessage: (message: string) => void
  volume: number
  muted: boolean
  onVolumeChange: (volume: number) => void
  onToggleMute: () => void
}

function formatTime(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '--:--.-'
  const minutes = Math.floor(value / 60)
  const seconds = (value % 60).toFixed(1).padStart(4, '0')
  return `${minutes}:${seconds}`
}

/**
 * 사용자가 영상을 직접 탐색하면서 A/B 시각을 잡는 편집 전용 플레이어입니다.
 * 공유 연습 플레이어와 달리 YouTube 기본 조작 버튼을 노출합니다.
 */
export const YouTubeAuthoringPreview = forwardRef<YouTubeAuthoringPreviewHandle, Props>(
  function YouTubeAuthoringPreview({ youtubeUrl, title, onDurationChange, onMessage, volume, muted, onVolumeChange, onToggleMute }, ref) {
    const { t } = useI18n()
    const hostRef = useRef<HTMLDivElement | null>(null)
    const playerRef = useRef<YTPlayer | null>(null)
    const pollRef = useRef<number | null>(null)
    const previewEndRef = useRef<number | null>(null)
    const durationRef = useRef<number | null>(null)
    const readyRef = useRef(false)
    const [ready, setReady] = useState(false)
    const [playing, setPlaying] = useState(false)
    const [currentTime, setCurrentTime] = useState<number | null>(null)
    const [duration, setDuration] = useState<number | null>(null)
    const [status, setStatus] = useState(() => t('authoring.youtube.enterUrl'))
    const videoId = useMemo(() => youtubeVideoId(youtubeUrl), [youtubeUrl])
    const hasInput = youtubeUrl.trim().length > 0

    useImperativeHandle(ref, () => ({
      getCurrentTime() {
        if (!readyRef.current || !playerRef.current) return null
        const value = playerRef.current.getCurrentTime()
        return Number.isFinite(value) ? value : null
      },
      getDuration() {
        return durationRef.current
      },
      previewRange(startTime, endTime) {
        const player = playerRef.current
        const knownDuration = durationRef.current
        if (!readyRef.current || !player || knownDuration == null) {
          return t('authoring.youtube.notReady')
        }
        const error = segmentRangeError(startTime, endTime, knownDuration)
        if (error) return t('authoring.error.segmentRangeGeneric')

        previewEndRef.current = endTime
        player.seekTo(startTime, true)
        player.playVideo()
        const nextStatus = t('authoring.youtube.previewRange', { start: formatTime(startTime), end: formatTime(endTime) })
        setStatus(nextStatus)
        onMessage(nextStatus)
        return null
      },
    }), [onMessage])

    useEffect(() => {
      readyRef.current = false
      durationRef.current = null
      previewEndRef.current = null
      setReady(false)
      setPlaying(false)
      setCurrentTime(null)
      setDuration(null)
      onDurationChange(null)

      if (!hasInput) {
        setStatus(t('authoring.youtube.enterUrl'))
        return
      }
      if (!videoId || !hostRef.current) {
        setStatus(t('authoring.youtube.invalidUrl'))
        return
      }

      let cancelled = false
      void loadYouTubeIframeApi().then((YT) => {
        if (cancelled || !hostRef.current) return
        const mountNode = document.createElement('div')
        hostRef.current.replaceChildren(mountNode)
        playerRef.current = new YT.Player(mountNode, {
          videoId,
          playerVars: {
            controls: 1,
            playsinline: 1,
            rel: 0,
            modestbranding: 1,
          },
          events: {
            onReady: () => {
              if (cancelled || !playerRef.current) return
              readyRef.current = true
              playerRef.current.setVolume(volume)
              setReady(true)
              const loadedDuration = playerRef.current.getDuration()
              if (Number.isFinite(loadedDuration) && loadedDuration > 0) {
                durationRef.current = loadedDuration
                setDuration(loadedDuration)
                onDurationChange(loadedDuration)
              }
              const nextStatus = t('authoring.youtube.readyHint')
              setStatus(nextStatus)
              onMessage(nextStatus)
            },
            onStateChange: (event) => {
              if (cancelled) return
              setPlaying(event.data === YT.PlayerState.PLAYING)
              if (event.data === YT.PlayerState.PLAYING && previewEndRef.current == null) {
                setStatus(t('authoring.youtube.selectingPosition'))
              }
            },
            onError: () => {
              if (cancelled) return
              readyRef.current = false
              setReady(false)
              setPlaying(false)
              const nextStatus = t('authoring.youtube.unavailable')
              setStatus(nextStatus)
              onMessage(nextStatus)
            },
          },
        })

        pollRef.current = window.setInterval(() => {
          const player = playerRef.current
          if (!player || !readyRef.current) return

          const nextCurrentTime = player.getCurrentTime()
          if (Number.isFinite(nextCurrentTime)) setCurrentTime(nextCurrentTime)

          const nextDuration = player.getDuration()
          if (Number.isFinite(nextDuration) && nextDuration > 0 && nextDuration !== durationRef.current) {
            durationRef.current = nextDuration
            setDuration(nextDuration)
            onDurationChange(nextDuration)
          }

          const previewEnd = previewEndRef.current
          if (previewEnd != null && nextCurrentTime >= previewEnd - 0.04) {
            player.pauseVideo()
            previewEndRef.current = null
            const nextStatus = t('authoring.message.previewFinished')
            setStatus(nextStatus)
            onMessage(nextStatus)
          }
        }, 100)
      }).catch(() => {
        if (cancelled) return
        const nextStatus = t('authoring.youtube.loadFailed')
        setStatus(nextStatus)
        onMessage(nextStatus)
      })

      return () => {
        cancelled = true
        readyRef.current = false
        if (pollRef.current != null) window.clearInterval(pollRef.current)
        pollRef.current = null
        playerRef.current?.destroy()
        playerRef.current = null
        hostRef.current?.replaceChildren()
      }
    }, [videoId, hasInput, onDurationChange, onMessage])

    useEffect(() => {
      if (readyRef.current) playerRef.current?.setVolume(volume)
    }, [volume])

    function seekBy(seconds: number) {
      const player = playerRef.current
      if (!readyRef.current || !player) return

      // 수동으로 위치를 옮기면 실행 중이던 구간 미리 재생은 해제합니다.
      previewEndRef.current = null
      const target = clampSeekTime(player.getCurrentTime(), durationRef.current, seconds)
      player.seekTo(target, true)
      setCurrentTime(target)
      const nextStatus = t(seconds < 0 ? 'playback.seekBackward' : 'playback.seekForward', { seconds: Math.abs(seconds) })
      setStatus(nextStatus)
      onMessage(nextStatus)
    }

    function play() {
      const player = playerRef.current
      if (!readyRef.current || !player) return
      previewEndRef.current = null
      player.playVideo()
      setStatus(t('authoring.youtube.playing'))
    }

    function stop() {
      const player = playerRef.current
      if (!player) return
      previewEndRef.current = null
      player.pauseVideo()
      setPlaying(false)
      setStatus(t('authoring.youtube.stopped'))
    }

    return (
      <section className="youtube-authoring-preview">
        <div className="youtube-authoring-player">
          {videoId ? <div key={videoId} ref={hostRef} title={title || t('authoring.youtube.previewTitle')} /> : <div className="youtube-authoring-placeholder">YouTube</div>}
        </div>
        <PlaybackSeekControls
          onSeek={seekBy}
          onPlay={play}
          onStop={stop}
          disabled={!ready}
          playing={playing}
          playLabel={t('authoring.youtube.play')}
          stopLabel={t('authoring.youtube.stop')}
        />
        <div className="youtube-authoring-status" aria-live="polite">
          <strong>{t('authoring.youtube.currentTime', { time: formatTime(currentTime) })}</strong>
          <span>{t('authoring.youtube.totalTime', { time: formatTime(duration) })}</span>
          <span className={ready ? 'badge' : 'muted'}>{ready ? t('authoring.youtube.ready') : status}</span>
          <VolumeControl
            volume={volume}
            muted={muted}
            onVolumeChange={onVolumeChange}
            onToggleMute={onToggleMute}
            className="compact"
          />
        </div>
        {ready && <p className="muted">{t('authoring.youtube.seekHint')}</p>}
      </section>
    )
  },
)
